import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useNotificationManager } from './hooks/useNotificationManager';
import { useRealtimeSync } from './hooks/useRealtimeSync';
import Layout from './components/Layout';
import ClientPortalLayout from './components/ClientPortalLayout';
import LoadingSpinner from './components/common/LoadingSpinner';
import { ThemeProvider } from './contexts/ThemeContext';
import { NudgeIcon } from './components/common/NudgeLogo';
import OfflineBanner from './components/common/OfflineBanner';
import UndoToast from './components/common/UndoToast';
import ToastContainer from './components/common/ToastContainer';
import ConfirmClientWillSeeModal from './components/common/ConfirmClientWillSeeModal';
import NotificationDetailProvider from './components/common/NotificationDetailProvider';
import ErrorBoundary from './components/common/ErrorBoundary';

// Lazy-loaded Pages (Code Splitting)
const LoginPage = lazy(() => import('./pages/LoginPage'));
const InstagramPage = lazy(() => import('./pages/InstagramPage'));
const ClientPortalPage = lazy(() => import('./pages/ClientPortalPage'));
const ClientAccountPage = lazy(() => import('./pages/ClientAccountPage'));
const Settings = lazy(() => import('./pages/SettingsV2'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
const MissionsPage = lazy(() => import('./pages/MissionsPage'));
const JournalPage = lazy(() => import('./pages/JournalPage'));
const ClientsDashboardPage = lazy(() => import('./pages/ClientsDashboardPage'));
const FinancePage = lazy(() => import('./pages/FinancePage'));
const PublicMediaValidationPage = lazy(() => import('./pages/PublicMediaValidationPage'));
const BufferConnectionsPage = lazy(() => import('./pages/admin/BufferConnectionsPage'));

function App() {
  const { isAuthenticated, isLoading, verifyToken, user } = useAuthStore();

  // Initialiser le gestionnaire de notifications
  useNotificationManager();

  // Temps-réel : synchro live des missions (et + à venir) via Socket.IO. Actif seulement si connecté.
  useRealtimeSync(isAuthenticated);

  useEffect(() => {
    console.log('🔄 App useEffect - État actuel:', { isAuthenticated, user: user?.email, isLoading });
    // Vérifier le token au démarrage et déconnecter si invalide
    if (isAuthenticated) {
      verifyToken();
    }

    // Vérification périodique du statut utilisateur (toutes les 5 minutes)
    if (isAuthenticated) {
      const intervalId = setInterval(() => {
        console.log('🔍 Vérification périodique du statut utilisateur');
        verifyToken();
      }, 5 * 60 * 1000); // 5 minutes

      return () => clearInterval(intervalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  console.log('🎯 App render - État:', { isAuthenticated, user: user?.email, isLoading });

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-neutral-950">
        <NudgeIcon className="w-16 h-16 animate-pulse-subtle" />
        <span className="mt-4 text-lg font-bold text-primary-600 dark:text-white tracking-tight">NUDGE</span>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <Router>
        <OfflineBanner />
        <UndoToast />
        <ToastContainer />
        <ConfirmClientWillSeeModal />
        <NotificationDetailProvider />
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950 transition-colors duration-200">
          <ErrorBoundary>
          <Suspense fallback={
            <div className="min-h-screen flex flex-col items-center justify-center">
              <NudgeIcon className="w-12 h-12 animate-pulse-subtle" />
            </div>
          }>
            <Routes>
            {/* Route publique pour OAuth callback (pas d'auth requise) */}
            <Route path="/settings/late/callback" element={<OAuthCallback />} />

            {/* Page publique de validation client par token (no auth, pour clients sans compte) */}
            <Route path="/m/:token" element={<PublicMediaValidationPage />} />

            {!isAuthenticated ? (
              <>
                <Route path="/login" element={<LoginPage />} />
                {/* Page de connexion UNIQUE : /login-client garde l'URL (liens déjà envoyés) mais
                    rend la même page. La redirection par rôle se fait après login. */}
                <Route path="/login-client" element={<LoginPage />} />
                <Route path="/reset-password" element={<LoginPage />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </>
            ) : user?.role === 'CLIENT' ? (
              <Route path="/" element={<ClientPortalLayout />}>
                <Route index element={<Navigate to="/client-portal" replace />} />
                <Route path="/client-portal" element={<ClientPortalPage />} />
                <Route path="/client-portal/account" element={<ClientAccountPage />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="*" element={<Navigate to="/client-portal" replace />} />
              </Route>
            ) : (
              <Route path="/" element={<Layout />}>
                <Route index element={<Navigate to="/instagram" replace />} />
                <Route path="/instagram" element={<InstagramPage />} />
                <Route path="/missions" element={<MissionsPage />} />
                <Route path="/journal" element={<JournalPage />} />
                {/* /missions/:id (ancienne page détail) → redirige vers la liste.
                    Le détail vit désormais dans le board (Canvas/Kanban) du client. */}
                <Route path="/missions/:id" element={<Navigate to="/missions" replace />} />
                <Route path="/clients" element={<ClientsDashboardPage />} />
                <Route path="/finance" element={<FinancePage />} />
                <Route path="/connexions-buffer" element={<BufferConnectionsPage />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="*" element={<Navigate to="/instagram" replace />} />
              </Route>
            )}
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
